// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import isEqual from 'lodash.isequal';

import Modal from './Modal';
import SelectDate from '../SelectDate';

import api from '../../api';

import '../../styles/forms.css';
import '../../styles/intake-forms.css';

export default class FieldForm extends Modal {

    state = {
        section: 0,
        metadata: [],
        form: {}
    }

    componentDidMount = () => {
        if (!this.props.data) return;
            this.loadData(this.props.data.form._id, this.props.data.metadata);
    }

    componentWillReceiveProps = (next) => {
        if (!next.data) return;
        if (!isEqual(next.metadata, this.props.metadata))
            this.loadData(next.form._id, next.metadata);
    }
    
    loadData = async (formId, metadata) => {
        await this.props.loading(1005);
        const res = await api.get("/form/get?formId=" + formId);
        if (res && res.form)
            this.setState({ metadata, form: res.form });
        this.props.loaded();
    }

    getFieldValue = (key) => {
        const lower = key.toLowerCase();
        const field = this.state.metadata.find(m => m.key === lower);
        if (!field) return "";
        return field.value;
    }

    setFieldValue = (key, value) => {
        const lower = key.toLowerCase();
        if (!this.state.metadata.find(m => m.key === lower))
            return this.setState({
                metadata: [
                    ...this.state.metadata,
                    { key: lower, value }
                ]
            });

        let metadata = [ ...this.state.metadata ];
        for (const [i,f] of metadata.entries()) {
            if (f.key === lower) {
                metadata[i].value = value;
                break;
            }
        }
        this.setState({ metadata });
    }

    requiredFilled = (s) => {
        for (const field of s.fields) {
            if (field.dataType !== "checkbox" && field.required && !this.state.metadata.find(m => m.key === field.key.toLowerCase())) {
                toast.error(<>All required (<span style={{ color: 'rgb(200,0,0)', fontWeight: 600 }}>*</span>) fields must be filled before continuing!</>);
                return false;
            }
        }
        return true;
    }

    required = (field) => field.required ? <span className="form-live-field-required">✱</span> : "";

    renderContent = () => {
        const f = this.state.form;
        if (!f || !f._id || !f.sections) return <></>;

        const s = this.state.section < f.sections.length ? f.sections[this.state.section] : {
            title: "Error - Section does not exist",
            description: "",
            fields: []
        };

        return (<div className="modal-content modal-center">
            
            <h1>{f.title}</h1>
            {f.description !== "" ? <p>{f.description}</p> : ""}

            <div className="form-section-bar">
                <div
                    className="form-section-bar-filled"
                    style={{ width: String(100 * (this.state.section+1) / f.sections.length) + "%" }}
                />
            </div>

            {this.state.section > 0 ? <div
                className="form-section-back"
                onClick={() => this.setState({ section: this.state.section-1 })}
            >‹ Back</div> : ""}
            {this.state.section < f.sections.length-1 ? <div
                className="form-section-next"
                onClick={() => {
                    if (!this.requiredFilled(s)) return;
                    this.setState({ section: this.state.section+1 });
                }}
            >Next ›</div> : ""}

            <h2>{s.title} ({this.state.section+1}/{f.sections.length})</h2>
            {s.description !== "" ? <p>{s.description}</p> : ""}

            <div className="form-live-container">
                {s.fields.map((field,idx) => {
                    const half = ["textbox-half", "date", "dropdown"].includes(field.dataType);
                    const value = this.getFieldValue(field.key);
                    return<React.Fragment key={idx}>
                        {half ? "" : <div style={{ clear: 'both' }} />}
                        <div className={"form-live-field" + (half ? " form-live-field-half" : "")}>
                            {field.dataType !== "checkbox" ? <label>{field.label}{this.required(field)}</label> : ""}

                            <div style={{ clear: 'both' }} />
                            {["textbox", "textbox-half"].includes(field.dataType) ? <input
                                type="text"
                                className={"form-input-text" + (half ? "-half" : "")}
                                value={value}
                                onChange={e => this.setFieldValue(field.key, e.target.value)}
                                // placeholder={field.hint}
                            /> :
                            
                            field.dataType === "dropdown" ? <Select
                                className="select-container"
                                classNamePrefix="select"
                                value={{
                                    value,
                                    label: value
                                }}
                                options={field.options ? field.options.map(opt => ({
                                    label: opt,
                                    value: opt
                                })) : []}
                                isSearchable={true}
                                // placeholder={field.hint}
                                onChange={option => this.setFieldValue(field.key, option.value)}
                            /> :

                            field.dataType === "checkbox" ? <>
                                <label
                                    className="checkbox form-live-field-checkbox"
                                    title={field.label}
                                >{field.label}{this.required(field)}
                                    <input
                                        type="checkbox"
                                        checked={value === "Yes"}
                                        onChange={() => this.setFieldValue(field.key, value === "Yes" ? "No" : "Yes")}
                                    />
                                    <span className="checkmark" />
                                </label>
                                <div style={{ clear: 'both' }} />
                            </> :

                            field.dataType === "date" ? <div className="form-live-field-date">
                                <SelectDate
                                    value={value}
                                    onChange={(date) => this.setFieldValue(field.key, date.toString())}
                                />
                            </div> :

                            field.dataType === "textarea" ? <textarea
                                className="form-input-textarea"
                                value={value}
                                onChange={e => this.setFieldValue(field.key, e.target.value)}
                            /> :

                            ""}

                            {field.hint !== "" ? <div
                                className="form-live-field-hint"
                                style={field.dataType === "checkbox" ? {
                                    paddingLeft: 28
                                } : {}}
                            >{field.hint}</div> : ""}
                        </div>
                    </React.Fragment>;
                })}
            </div>

            <div style={{ clear: 'both', height: 40 }} />
            <div className="form-live-button">
                <div
                    className="button"
                    style={{
                        float: 'none',
                        backgroundImage: "url('https://static.stax.ai/images/icons/reviewed.png')",
                    }}
                    onClick={async () => {
                        // Ensure no required fields are left empty
                        if (!this.requiredFilled(s))
                            return;

                        await this.props.loading(1005);
                        await this.props.updateDocument(this.props.data.docId, { metadata: this.state.metadata });
                        this.props.loaded();

                        // Next section?
                        if (this.state.section < f.sections.length-1)
                            this.setState({ section: this.state.section+1 });
                        else this.props.modal(); // Close
                    }}
                >Save and {this.state.section === f.sections.length-1 ? "Close" : "Continue"}</div>
            </div>

        </div>);
    }

};